// Display rules for the tenant-wide system banner (ADR-0051). The banner arrives as
// { severity, message: { [language]: text }, start, end, dismissible, showOnSignIn };
// "no banner" is null, or the backend's response with every field null.

// The backend's severities, mapped onto MUI Alert's.
export const BANNER_ALERT_SEVERITY = Object.freeze({ info: "info", warning: "warning", critical: "error" });

// A timer longer than about 24.8 days fires at once, so a far-away start is re-checked daily.
const MAX_TIMER_MS = 24 * 60 * 60 * 1000;

const texts = (banner) => Object.entries(banner?.message ?? {}).filter(([, text]) => text?.trim());

const toTime = (value) => (value ? Date.parse(value) : null);

export const hasBanner = (banner) => texts(banner).length > 0;

// The reader's language ("nl-NL" counts as "nl"), else English, else whichever has text.
export const bannerMessage = (banner, language = "") => {
  const available = Object.fromEntries(texts(banner));
  const wanted = [language.split("-")[0], "en", ...Object.keys(available)];
  return available[wanted.find((code) => available[code])]?.trim() ?? "";
};

// The display window is resolved here, in the browser, against its own clock: a scheduled
// banner needs no server job (an accepted trade-off in ADR-0051).
export const isBannerActive = (banner, now) => {
  if (!hasBanner(banner)) return false;
  const start = toTime(banner.start);
  const end = toTime(banner.end);
  return (start === null || now >= start) && (end === null || now < end);
};

// Milliseconds until the banner next appears or disappears; null when it never will.
export const msUntilBannerChange = (banner, now) => {
  if (!hasBanner(banner)) return null;
  const upcoming = [toTime(banner.start), toTime(banner.end)].filter((time) => time !== null && time > now);
  return upcoming.length ? Math.min(Math.min(...upcoming) - now, MAX_TIMER_MS) : null;
};

// The sessionStorage key of the banner the reader closed, holding its bannerSignature.
export const SYSTEM_BANNER_DISMISSED_KEY = "systemBanner.dismissed";

// What a dismissal remembers: a changed text, severity or window shows the banner again.
export const bannerSignature = (banner) =>
  JSON.stringify([banner?.severity, banner?.message, banner?.start, banner?.end]);

// The admin layout publishes the banner's height in this CSS variable (unset elsewhere), so
// pages that fill the viewport below the top nav can leave room for it.
export const SYSTEM_BANNER_HEIGHT_VAR = "--system-banner-height";
export const SYSTEM_BANNER_HEIGHT = `var(${SYSTEM_BANNER_HEIGHT_VAR}, 0px)`;
